import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Sparkles, Users, BarChart3, Cloud, Check, ArrowRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';

const FeaturesPage = () => {
  return (
    <>
      {/* Header */}
      <section className="py-20 px-6 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl font-bold mb-6">
            Everything you need to{' '}
            <span className="bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
              run your property
            </span>
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Keyless PMS brings reservations, operations, and reporting into one simple platform. Here's what you get.
          </p>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto space-y-32">
          {/* Reservations & Calendar */}
          <div>
            <div className="mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-100 text-cyan-700 rounded-full text-sm font-medium mb-4">
                <Calendar size={18} />
                Reservations & Calendar
              </div>
              <h2 className="text-3xl font-bold mb-4">Manage all your bookings in one visual calendar</h2>
              <p className="text-lg text-slate-600 max-w-3xl">
                See arrivals, departures, and room availability at a glance. Create reservations from phone calls, walk-ins, or online inquiries in seconds.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Card>
                <CardContent className="pt-6">
                  <Check className="text-cyan-600 mb-3" size={24} />
                  <h3 className="font-bold mb-2">Interactive tape chart calendar</h3>
                  <p className="text-sm text-slate-600">
                    Visual grid showing all rooms and dates, color-coded by booking status
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <Check className="text-cyan-600 mb-3" size={24} />
                  <h3 className="font-bold mb-2">Quick booking creation</h3>
                  <p className="text-sm text-slate-600">
                    Click any date to create a reservation with guest details, room selection, and rate
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <Check className="text-cyan-600 mb-3" size={24} />
                  <h3 className="font-bold mb-2">Check-in and check-out workflows</h3>
                  <p className="text-sm text-slate-600">
                    One-click check-in and check-out from the Today view
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <Check className="text-cyan-600 mb-3" size={24} />
                  <h3 className="font-bold mb-2">Group bookings</h3>
                  <p className="text-sm text-slate-600">
                    Reserve multiple rooms for groups or events
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <Check className="text-cyan-600 mb-3" size={24} />
                  <h3 className="font-bold mb-2">Reservation details</h3>
                  <p className="text-sm text-slate-600">
                    Store guest contact info, special requests, payment records, and notes
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <Check className="text-cyan-600 mb-3" size={24} />
                  <h3 className="font-bold mb-2">Guest portal</h3>
                  <p className="text-sm text-slate-600">
                    Guests can view their reservation details, check-in online, and make requests
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Housekeeping & Maintenance */}
          <div>
            <div className="mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-4">
                <Sparkles size={18} />
                Housekeeping & Maintenance
              </div>
              <h2 className="text-3xl font-bold mb-4">Track room cleanliness and maintenance in real time</h2>
              <p className="text-lg text-slate-600 max-w-3xl">
                Generate daily housekeeping lists automatically. Create work orders and keep rooms in top condition.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Card>
                <CardContent className="pt-6">
                  <Check className="text-blue-600 mb-3" size={24} />
                  <h3 className="font-bold mb-2">Room status tracking</h3>
                  <p className="text-sm text-slate-600">
                    Mark rooms as Clean, Dirty, In Progress, or Out of Order
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <Check className="text-blue-600 mb-3" size={24} />
                  <h3 className="font-bold mb-2">Auto-generated housekeeping lists</h3>
                  <p className="text-sm text-slate-600">
                    Daily task lists for housekeeping staff based on checkouts and room status
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <Check className="text-blue-600 mb-3" size={24} />
                  <h3 className="font-bold mb-2">Real-time updates</h3>
                  <p className="text-sm text-slate-600">
                    Front desk sees room status changes immediately
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <Check className="text-blue-600 mb-3" size={24} />
                  <h3 className="font-bold mb-2">Maintenance work orders</h3>
                  <p className="text-sm text-slate-600">
                    Create tickets for repairs with priority levels, descriptions, and photos
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <Check className="text-blue-600 mb-3" size={24} />
                  <h3 className="font-bold mb-2">Work order assignment</h3>
                  <p className="text-sm text-slate-600">
                    Assign tickets to maintenance staff and track progress
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <Check className="text-blue-600 mb-3" size={24} />
                  <h3 className="font-bold mb-2">Asset management</h3>
                  <p className="text-sm text-slate-600">
                    Track maintenance history for HVAC, plumbing, electrical systems, and equipment
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Staff & Permissions */}
          <div>
            <div className="mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-medium mb-4">
                <Users size={18} />
                Staff & Permissions
              </div>
              <h2 className="text-3xl font-bold mb-4">Invite unlimited team members with role-based access</h2>
              <p className="text-lg text-slate-600 max-w-3xl">
                Control who sees what with role-based permissions. Track staff activity and schedules.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Card>
                <CardContent className="pt-6">
                  <Check className="text-purple-600 mb-3" size={24} />
                  <h3 className="font-bold mb-2">Unlimited users</h3>
                  <p className="text-sm text-slate-600">
                    Invite as many staff members as you need at no extra cost
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <Check className="text-purple-600 mb-3" size={24} />
                  <h3 className="font-bold mb-2">Role-based access</h3>
                  <p className="text-sm text-slate-600">
                    Manager, Front Desk, Housekeeping, and Maintenance roles with appropriate permissions
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <Check className="text-purple-600 mb-3" size={24} />
                  <h3 className="font-bold mb-2">Staff scheduling</h3>
                  <p className="text-sm text-slate-600">
                    Create shifts for front desk, housekeeping, and maintenance teams
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <Check className="text-purple-600 mb-3" size={24} />
                  <h3 className="font-bold mb-2">Audit logs</h3>
                  <p className="text-sm text-slate-600">
                    Track all user activity—who checked in a guest, updated a rate, or marked a room clean
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <Check className="text-purple-600 mb-3" size={24} />
                  <h3 className="font-bold mb-2">Mobile-friendly</h3>
                  <p className="text-sm text-slate-600">
                    Staff can access their tasks and update status from tablets or mobile browsers
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <Check className="text-purple-600 mb-3" size={24} />
                  <h3 className="font-bold mb-2">Team communication</h3>
                  <p className="text-sm text-slate-600">
                    Internal messaging for staff coordination (Pro plan)
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Reporting & Forecasting */}
          <div>
            <div className="mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-pink-100 text-pink-700 rounded-full text-sm font-medium mb-4">
                <BarChart3 size={18} />
                Reporting & Forecasting
              </div>
              <h2 className="text-3xl font-bold mb-4">Understand your property's performance</h2>
              <p className="text-lg text-slate-600 max-w-3xl">
                Real-time dashboards and reports. Forecast occupancy and revenue for better decision-making. Export data for accounting.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Card>
                <CardContent className="pt-6">
                  <Check className="text-pink-600 mb-3" size={24} />
                  <h3 className="font-bold mb-2">Real-time dashboard</h3>
                  <p className="text-sm text-slate-600">
                    Occupancy rate, revenue, ADR, and RevPAR displayed prominently
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <Check className="text-pink-600 mb-3" size={24} />
                  <h3 className="font-bold mb-2">Occupancy forecasting</h3>
                  <p className="text-sm text-slate-600">
                    Predict occupancy for the next 30, 60, or 90 days based on bookings and historical data
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <Check className="text-pink-600 mb-3" size={24} />
                  <h3 className="font-bold mb-2">Revenue analytics</h3>
                  <p className="text-sm text-slate-600">
                    Track revenue trends by date, room type, or rate plan
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <Check className="text-pink-600 mb-3" size={24} />
                  <h3 className="font-bold mb-2">CSV/PDF exports</h3>
                  <p className="text-sm text-slate-600">
                    Export reservations, occupancy reports, maintenance logs, and guest data
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <Check className="text-pink-600 mb-3" size={24} />
                  <h3 className="font-bold mb-2">Advanced analytics</h3>
                  <p className="text-sm text-slate-600">
                    Cohort analysis, guest lifetime value, channel attribution (Pro plan)
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <Check className="text-pink-600 mb-3" size={24} />
                  <h3 className="font-bold mb-2">Custom report builder</h3>
                  <p className="text-sm text-slate-600">
                    Create and save custom reports with filters (Pro plan)
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Multi-Property & Cloud */}
          <div>
            <div className="mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium mb-4">
                <Cloud size={18} />
                Multi-Property & Cloud
              </div>
              <h2 className="text-3xl font-bold mb-4">Manage multiple properties from one account</h2>
              <p className="text-lg text-slate-600 max-w-3xl">
                Access your PMS from any device with a browser. No installation, no servers, no IT headaches.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Card>
                <CardContent className="pt-6">
                  <Check className="text-indigo-600 mb-3" size={24} />
                  <h3 className="font-bold mb-2">Multi-property management</h3>
                  <p className="text-sm text-slate-600">
                    Switch between properties instantly from a single login (Pro plan)
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <Check className="text-indigo-600 mb-3" size={24} />
                  <h3 className="font-bold mb-2">Cloud-based</h3>
                  <p className="text-sm text-slate-600">
                    No software to install or update. Access from any computer, tablet, or laptop
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <Check className="text-indigo-600 mb-3" size={24} />
                  <h3 className="font-bold mb-2">Works anywhere</h3>
                  <p className="text-sm text-slate-600">
                    All you need is a web browser and internet connection
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <Check className="text-indigo-600 mb-3" size={24} />
                  <h3 className="font-bold mb-2">Auto-save</h3>
                  <p className="text-sm text-slate-600">
                    Your data is saved automatically as you work
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <Check className="text-indigo-600 mb-3" size={24} />
                  <h3 className="font-bold mb-2">Data security</h3>
                  <p className="text-sm text-slate-600">
                    Encrypted connections, secure cloud storage, regular backups
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <Check className="text-indigo-600 mb-3" size={24} />
                  <h3 className="font-bold mb-2">Property-level reporting</h3>
                  <p className="text-sm text-slate-600">
                    View dashboards and reports for individual properties or roll them up across your portfolio
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 bg-gradient-to-br from-cyan-600 to-blue-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">See Keyless PMS in action</h2>
          <p className="text-xl mb-8 opacity-90">
            Get started with a 14-day free trial. No credit card required. Full access to all features.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/signup">
              <Button size="lg" className="bg-white text-cyan-700 hover:bg-slate-100 w-full sm:w-auto">
                Start free trial
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 w-full sm:w-auto">
              Book a demo
            </Button>
          </div>
          <p className="text-sm opacity-75 mt-6">Available on Starter, Pro, and Enterprise plans</p>
        </div>
      </section>
    </>
  );
};

export default FeaturesPage;